import React, { useMemo } from "react";
import "./Plugins.scss";
import { Card, Container, Button, NavBar, ConfirmationDialog, DataGrid, Icon, CardHeader } from "../../shared/components";
import * as coreApi from "../../shared/core-api-client";
import * as _ from "lodash";
import EditPluginDialog from "./EditPlugin";
import { connect } from "react-redux";
import { showSuccess, showError } from "../../store/notifications/actions";
import { useSettings } from "../../shared/providers/SettingsProvider";
import RequiresTags from "../../shared/components/RequiresTags";

function Plugins(props) {
    const [refreshCounter, setRefreshCounter] = React.useState(0);
    const [data, setData] = React.useState<coreApi.PluginListModel[]>([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [showEdit, setShowEdit] = React.useState(false);
    const [activeItem, setActiveItem] = React.useState(null);
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const settings = useSettings();
    const pluginsClient = useMemo(() => new coreApi.PluginsClient(settings.coreApiUrl), [settings.coreApiUrl]);

    React.useEffect(() => {
        setIsLoading(true);
        pluginsClient.search(undefined, undefined, undefined, settings.tenantId)
            .then(result => setData(result))
            .finally(() => setIsLoading(false));
    }, [refreshCounter]);

    const onPluginCreatedOrUpdated = () => {
        setRefreshCounter(refreshCounter + 1);
    }

    const createOrEditPlugin = (model) => {
        setActiveItem(model);
        setShowEdit(true);
    }

    const deletePlugin = (model) => {
        setActiveItem(model);
        setConfirmOpen(true);
    }

    const onDeleteConfirmed = () => {
        pluginsClient.deleteById(activeItem.id)
            .then(() => {
                props.showSuccess("Plugin deleted successfully.");
                setRefreshCounter(refreshCounter + 1);
            })
            .catch(error => {
                props.showError("Unable to delete plugin.  Please try again.");
            });
    }

    return (
        <>
            <NavBar
                title="Manage Plugins">
            </NavBar>

            <Container className="plush plugins-list" maxWidth="lg">
                <Card className="col-fill">
                    <CardHeader title="Plugin List" action={
                        <RequiresTags tags={[["tenants::update"]]}>
                            <Button className="align-self-center" size="small" variant="contained" color="success" onClick={() => createOrEditPlugin(null)}>
                                <Icon fontSize="small">add</Icon>
                                Add Plugin
                            </Button>
                        </RequiresTags>
                    } />
                    <DataGrid
                        disableRowSelectionOnClick
                        loading={isLoading}
                        columns={[
                            { headerName: "Name", field: "name", width: 180 },
                            { headerName: "Description", field: "description", flex: 1 },
                            { headerName: "# of URL Mapping Rules", field: "branchMappingsCount", width: 180 },
                            { headerName: "Is Enabled?", field: "isEnabled", cellClassName: "justify-content-center", renderCell: (e) => e.row.isEnabled ? "Yes" : "No" },
                            {
                                field: "actions", width: 150, type: "actions", getActions: (r) => [
                                    <RequiresTags tags={[["tenants::update"]]}>
                                        <Button size="small" variant="contained" color="primary" onClick={(e) => { createOrEditPlugin(r.row); e.preventDefault(); }} title="Edit Plugin"><Icon fontSize="small">edit</Icon></Button>
                                    </RequiresTags>,
                                    <RequiresTags tags={[["tenants::update"]]}>
                                        <Button size="small" variant="contained" color="error" onClick={(e) => { deletePlugin(r.row); e.preventDefault(); }} title="Delete Plugin"><Icon fontSize="small">delete</Icon></Button>
                                    </RequiresTags>
                                ]
                            }
                        ]}
                        rows={data}
                        initialState={{
                            sorting: {
                                sortModel: [
                                    { field: "name", sort: "asc" }
                                ]
                            }
                        }}
                    />
                </Card>
            </Container>

            <EditPluginDialog key={activeItem?.id} open={showEdit} setOpen={setShowEdit} plugin={activeItem} onSuccess={onPluginCreatedOrUpdated} />

            <ConfirmationDialog
                title="Delete Plugin?"
                open={confirmOpen}
                setOpen={setConfirmOpen}
                onConfirm={onDeleteConfirmed}
            >
                {`Are you sure you want to delete ${activeItem?.name}?`}
            </ConfirmationDialog>

        </>
    );
}

export default connect(
    null,
    { showSuccess, showError })(Plugins);